import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ExternalLink, CheckCircle2, Clock, CreditCard, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { http, euro } from "@/lib/api";

export default function OrderPage() {
  const { id } = useParams();
  const [o, setO] = useState(null);
  const [error, setError] = useState(false);
  const [params, setParams] = useSearchParams();
  const sessionId = params.get("session_id");
  const [checking, setChecking] = useState(!!sessionId);
  const [redirecting, setRedirecting] = useState(false);

  useEffect(() => {
    http.get(`/orders/${id}`).then((r) => setO(r.data)).catch(() => setError(true));
  }, [id]);

  useEffect(() => {
    if (!sessionId) return;
    let tries = 0;
    let timer;
    const poll = async () => {
      tries += 1;
      try {
        const { data } = await http.get(`/payments/status/${sessionId}`);
        if (data?.payment_status === "paid") {
          const r = await http.get(`/orders/${id}`);
          setO(r.data);
          setChecking(false);
          setParams({}, { replace: true });
          toast.success("Paiement par carte confirmé. Merci !");
          return;
        }
        if (data?.payment_status === "expired" || tries >= 10) {
          setChecking(false);
          return;
        }
      } catch {
        setChecking(false);
        return;
      }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId, id, setParams]);

  const payByCard = async () => {
    setRedirecting(true);
    try {
      const { data } = await http.post("/payments/checkout", { order_id: id, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (err) {
      setRedirecting(false);
      toast.error(err?.response?.data?.detail ?? "Paiement par carte indisponible, réessayez.");
    }
  };

  const declare = async () => {
    try {
      const { data } = await http.post(`/orders/${id}/declare-paid`);
      setO(data);
      toast.success("Merci ! Nous vérifions votre paiement et vous écrivons très vite.");
    } catch {
      toast.error("Une erreur est survenue, réessayez.");
    }
  };

  if (error) return <div data-testid="order-not-found" className="px-5 pt-40 pb-20 text-center font-display text-3xl">Commande introuvable.</div>;
  if (!o) return <div className="min-h-screen" />;
  const settled = ["paye", "expedie"].includes(o.status);
  const paid = settled || o.status === "paiement_declare";
  const lines = Array.isArray(o.lines) ? o.lines : [];

  return (
    <div data-testid="order-page" className="mx-auto max-w-3xl px-5 pt-[120px] pb-10 sm:px-10">
      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9 }}>
        <p className="font-mono-label text-[11px] text-[var(--muted)]">Commande N° {o.id?.slice(-8).toUpperCase()}</p>
        <h1 className="font-display mt-4 text-4xl sm:text-6xl font-light leading-[1.02] tracking-tight">
          {paid ? <>Merci, <em className="italic text-[var(--terra)]">{o.name?.split(" ")[0]}</em>.</> : <>Dernière étape : <em className="italic text-[var(--terra)]">le paiement</em>.</>}
        </h1>
        <p data-testid="order-status" className="mt-6 flex items-center gap-2 text-base text-[var(--muted)]">
          {checking ? <Loader2 className="h-5 w-5 animate-spin" /> : paid ? <CheckCircle2 className="h-5 w-5 text-[#4A6136]" /> : <Clock className="h-5 w-5" />}
          {checking ? "Vérification de votre paiement par carte…" : settled ? `Paiement reçu — confirmation envoyée à ${o.email}.` : paid ? `Paiement déclaré — confirmation envoyée à ${o.email} après vérification.` : "Votre commande est enregistrée et en attente de paiement."}
        </p>

        {!paid && !checking && (
          <div className="mt-10 rounded-[24px] border border-[var(--line)] bg-white p-6 sm:p-8">
            <p className="text-sm text-[var(--muted)]">Montant à régler</p>
            <p data-testid="order-total" className="font-display text-5xl">{euro(o.total)}</p>
            <button data-testid="card-pay-button" onClick={payByCard} disabled={redirecting}
              className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-[var(--ink)] py-4 text-base font-medium text-[var(--bone)] transition-colors hover:bg-[var(--terra)] disabled:opacity-60">
              {redirecting ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />} Payer par carte bancaire
            </button>
            <p className="mt-2 text-center text-xs text-[var(--muted)]">Visa, Mastercard, CB, Apple Pay · Paiement sécurisé Stripe · Confirmation instantanée</p>
            <div className="my-5 flex items-center gap-3 text-xs text-[var(--muted)]"><span className="h-px flex-1 bg-[var(--line)]" />ou<span className="h-px flex-1 bg-[var(--line)]" /></div>
            <a href={o.paypal_url} target="_blank" rel="noopener noreferrer" data-testid="paypal-pay-link"
              className="flex w-full items-center justify-center gap-2 rounded-full bg-[#FFC439] py-4 text-base font-semibold text-[#003087] transition-[filter] hover:brightness-95">
              Payer avec PayPal <ExternalLink className="h-4 w-4" />
            </a>
            <p className="mt-3 text-center text-xs text-[var(--muted)]">Compte PayPal ou carte bancaire · Indiquez « {o.id?.slice(-8).toUpperCase()} » en note</p>
            <button data-testid="declare-paid-button" onClick={declare}
              className="mt-6 w-full rounded-full border border-[var(--ink)] py-3 text-sm transition-colors hover:bg-[var(--ink)] hover:text-[var(--bone)]">
              J'ai payé avec PayPal
            </button>
          </div>
        )}

        <div className="mt-10">
          <p className="font-mono-label text-[11px] text-[var(--muted)]">Récapitulatif</p>
          <ul className="mt-4 divide-y divide-[var(--line)] border-y border-[var(--line)]">
            {lines.map((l) => (
              <li key={l.product_id} className="flex justify-between gap-4 py-4 text-sm">
                <span>{l.name} × {l.qty}</span><span>{euro(l.price * l.qty)}</span>
              </li>
            ))}
          </ul>
          <div className="mt-4 space-y-1 text-sm">
            <div className="flex justify-between text-[var(--muted)]"><span>Livraison</span><span>{o.shipping ? euro(o.shipping) : "Offerte"}</span></div>
            {o.service_fee > 0 && <div className="flex justify-between text-[var(--muted)]"><span>Frais de service</span><span data-testid="order-service-fee">{euro(o.service_fee)}</span></div>}
            {o.discount > 0 && <div className="flex justify-between text-[var(--terra-deep)]"><span>Remise {o.coupon_code}</span><span data-testid="order-discount">-{euro(o.discount)}</span></div>}
            <div className="flex justify-between font-display text-2xl"><span>Total</span><span>{euro(o.total)}</span></div>
          </div>
        </div>
        <Link to="/" data-testid="back-home-link" className="mt-12 inline-block text-sm underline underline-offset-4">Retour à l'accueil</Link>
      </motion.div>
    </div>
  );
}
